// Desktop popover half of the <x-select> component
// (resources/views/components/select.blade.php).
//
// The native <select> is the single source of truth and the thing that submits
// with the form. Its options come from either the :options array or a slot of
// raw <option>s - by this point both are just <option>s in the <select>. On
// load this script builds the popover from those options, keeps the two in sync
// (every choice is written back to the <select>, firing change), and adds
// .enhanced to the field so the stylesheet swaps the native control for the
// custom trigger + popover on desktop.
//
// Progressive enhancement: without this script the native <select> is shown at
// every screen size and is fully usable. Nothing here runs until the markup it
// enhances exists - see the onContentReady call at the foot of the file.

import { onContentReady } from './shared/contentLifecycle.js';

const FIELD_SELECTOR = '[data-select-field]';

function getParts(field)
{
    return {
        native: field.querySelector('.select_field_native'),
        label: field.querySelector('.label'),
        trigger: field.querySelector('.select_field_trigger'),
        popover: field.querySelector('.select_field_popover'),
        value: field.querySelector('.select_field_value'),
        // Built by buildOptions(); queried live so handlers see the current set.
        options: Array.from(field.querySelectorAll('.select_field_option')),
    };
}

// Build the popover's role="option" list from the native <select>'s options.
// Placeholder / disabled / empty-value entries are captions, not choices, so
// they are skipped. Idempotent - clears any previously-built options first.
function buildOptions(field)
{
    const { native, popover } = getParts(field);
    popover.querySelectorAll('.select_field_option').forEach(o => o.remove());

    Array.from(native.options).forEach((option, index) => {
        if (option.disabled || option.hidden || option.value === '')
        {
            return;
        }

        const item = document.createElement('div');
        item.className = 'select_field_option';
        item.id = native.id + '_option_' + index;
        item.setAttribute('role', 'option');
        item.setAttribute('data-value', option.value);
        item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
        item.textContent = option.text;
        popover.appendChild(item);
    });
}

function closeAllFields(except = null)
{
    document.querySelectorAll(FIELD_SELECTOR + '.open').forEach(field => {
        if (field !== except)
        {
            closeField(field);
        }
    });
}

function openField(field)
{
    const { trigger, popover, options } = getParts(field);

    closeAllFields(field); // only one open at a time
    field.classList.add('open');
    trigger.setAttribute('aria-expanded', 'true');

    // Start the keyboard highlight on the committed option (or the first one).
    const selected = options.find(o => o.getAttribute('aria-selected') === 'true');
    setActiveOption(field, selected || options[0]);
    popover.focus();
}

function closeField(field)
{
    if (!field.classList.contains('open'))
    {
        return;
    }
    const { trigger } = getParts(field);
    field.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
}

// The "active" option is the keyboard-highlighted one (aria-activedescendant) -
// distinct from the "selected" (committed) option below.
function setActiveOption(field, option)
{
    const { popover, options } = getParts(field);
    options.forEach(o => o.classList.remove('active'));
    if (!option)
    {
        return;
    }
    option.classList.add('active');
    popover.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
}

// Bring the custom side (trigger label + option aria-selected) into line with
// whatever the native <select> currently holds. This is the ONLY thing that
// updates the custom control, so the two can never drift - whether the value
// changed via the popover (desktop) or the native picker (mobile), it lands
// here through the <select>'s change event.
function syncFromNative(field)
{
    const { native, value, options } = getParts(field);
    if (!native)
    {
        return;
    }

    const current = native.value;
    const selected = options.find(o => o.getAttribute('data-value') === current);
    options.forEach(o => o.setAttribute('aria-selected', o === selected ? 'true' : 'false'));

    if (selected)
    {
        value.textContent = selected.textContent.trim();
        value.classList.remove('is_placeholder');
    }
    else
    {
        // Native is on its empty placeholder option - restore the placeholder look.
        value.textContent = value.dataset.placeholder || '';
        value.classList.add('is_placeholder');
    }
}

// Commit a choice from the popover: write it to the native <select> and fire
// change, which runs syncFromNative to update the custom side. syncFromNative is
// also called directly so a re-pick of the current value (no change event) still
// settles the UI.
function commitOption(field, option)
{
    const { native } = getParts(field);
    const newValue = option.getAttribute('data-value');

    if (native && native.value !== newValue)
    {
        native.value = newValue;
        native.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromNative(field);
    closeField(field);
    getParts(field).trigger.focus();
}

function onPopoverKeydown(event, field)
{
    const { options } = getParts(field);
    const active = options.find(o => o.classList.contains('active'));
    const index = options.indexOf(active);

    switch (event.key)
    {
        case 'ArrowDown':
            event.preventDefault();
            setActiveOption(field, options[Math.min(index + 1, options.length - 1)]);
            break;
        case 'ArrowUp':
            event.preventDefault();
            setActiveOption(field, options[Math.max(index - 1, 0)]);
            break;
        case 'Home':
            event.preventDefault();
            setActiveOption(field, options[0]);
            break;
        case 'End':
            event.preventDefault();
            setActiveOption(field, options[options.length - 1]);
            break;
        case 'Enter':
        case ' ':
            event.preventDefault();
            if (active)
            {
                commitOption(field, active);
            }
            break;
        case 'Escape':
            event.preventDefault();
            closeField(field);
            getParts(field).trigger.focus();
            break;
        case 'Tab':
            closeField(field); // let focus move on naturally
            break;
    }
}

function initField(field)
{
    const { native, label, trigger, popover } = getParts(field);
    if (!native || !trigger || !popover)
    {
        return;
    }

    buildOptions(field);

    // Options are built dynamically, so delegate their handlers to the popover.
    popover.addEventListener('click', (event) => {
        const option = event.target.closest('.select_field_option');
        if (option)
        {
            commitOption(field, option);
        }
    });
    popover.addEventListener('mousemove', (event) => {
        const option = event.target.closest('.select_field_option');
        if (option)
        {
            setActiveOption(field, option);
        }
    });
    popover.addEventListener('keydown', (event) => onPopoverKeydown(event, field));

    trigger.addEventListener('click', () => {
        field.classList.contains('open') ? closeField(field) : openField(field);
    });

    // Clicking the field label opens the picker too. On desktop (.enhanced) it
    // toggles the custom popover, mirroring the trigger. On mobile the native
    // <select> is shown, so ask the browser to open it - showPicker() does this
    // where supported; older browsers simply ignore the click (harmless).
    if (label)
    {
        label.addEventListener('click', () => {
            if (field.classList.contains('enhanced'))
            {
                field.classList.contains('open') ? closeField(field) : openField(field);
                return;
            }

            if (typeof native.showPicker === 'function')
            {
                try
                {
                    native.showPicker();
                }
                catch (error)
                {
                    // showPicker() throws if not user-activated or unsupported;
                    // there is no reliable fallback for opening a native select.
                }
            }
        });
    }

    // Open on ArrowDown/ArrowUp from the closed trigger (Enter/Space already
    // fire a native click on a <button>, which the handler above toggles).
    trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp')
        {
            event.preventDefault();
            openField(field);
        }
    });

    // Any change to the native <select> (the mobile picker, or programmatic)
    // updates the custom side - this keeps the two in step across a resize.
    native.addEventListener('change', () => syncFromNative(field));

    // Reflect the native <select>'s initial value, then reveal the custom UI.
    syncFromNative(field);
    field.classList.add('enhanced');
}

// Enhances the fields in the initial page, and in any content swapped in afterwards -
// a CMS section can carry a <x-select>, and the homepage's spa picker replaces content
// in place (docs/homepage-location-swap.md).
//
// Safe to run again over markup it has already seen: initField() ends by adding
// .enhanced, and the selector below excludes anything carrying it. Without that guard a
// second pass would build a second popover into the same field.
onContentReady((root) => {
    root.querySelectorAll(`${FIELD_SELECTOR}:not(.enhanced)`).forEach(initField);
});

// A click anywhere outside an open field closes it. Registered once, at module scope
// rather than inside the sweep above: it is delegated from the document, so it needs no
// knowledge of which fields exist and must not be added again per swap.
document.addEventListener('click', (event) => {
    if (!event.target.closest(FIELD_SELECTOR))
    {
        closeAllFields();
    }
});
